/**
 * PHA-795 (flow redesign): staged single-goal wizard for Voice of the Patient (VoP / PCM).
 *
 * Clinician-initiated from the Voice-of-the-Patient tab ("+ Create Voice of Patient Goals").
 * The wizard generates AI goal suggestions, the clinician picks ONE (or writes their own),
 * edits it, then generates the GAS scale + tracking question + barriers for that single goal,
 * and finally saves it for the care plan + survey.
 *
 * Stages:
 *   'suggest' — on open, calls extractPcmIntake for candidate goals (PHA-910: one per
 *               care-plan focus diagnosis plus a couple of whole-chart goals, with the
 *               patient's already-saved goals filtered out). The clinician selects
 *               exactly one via radio (or "Other" → free text).
 *   'edit'    — the chosen goal text is editable. "Generate Tracking Question/Barriers" calls
 *               generatePcmGoalDetails to build the tracking scaffolding for THAT goal.
 *   'refine'  — the single goal with its editable tracking_question + GAS scale + barriers.
 *               "Save for Care plan and survey" calls approvePcmIntake. PHA-851 made goals
 *               ADDITIVE: the goal is appended under a server-assigned pco_ref (ADD mode)
 *               or upserted in place (EDIT mode) — it is no longer a delete-all replace.
 *
 * Typography / design: Radix design system throughout; no MUI inside this panel.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  TextField,
  TextArea,
  Select,
  Card,
  RadioGroup,
  Heading,
  Separator,
  IconButton,
} from '@radix-ui/themes';
import { PlusIcon, TrashIcon, Pencil1Icon, CheckIcon } from '@radix-ui/react-icons';
import { toast } from 'react-hot-toast';
import { patientApi } from '../services/api';
import { TimedProgress, ProgressStep } from './TimedProgress';
import { filterNewGoalSuggestions, nextPcoRef } from '../utils/voiceOfPatientGoals';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type GasShape = Record<string, string | number | null | undefined>;

type GoalShape = {
  pco_ref: string;
  goal_text: string;
  tracking_question?: string;
  sort_order?: number;
  gas?: GasShape;
};

type BarrierShape = {
  barrier_ref: string;
  barrier_category: string;
  barrier_text: string;
  sort_order?: number;
};

/** Retained for callers that type VoP draft data (e.g. PatientDetail read view). */
export type PcmIntakeDraftShape = {
  goals: GoalShape[];
  barriers: BarrierShape[];
  confidence?: number;
  needs_manual_intake?: boolean;
};

const OTHER_REF = '__other__';

interface PcmIntakeReviewPanelProps {
  patientId: string;
  /** Optional patient-voice narrative to ground AI suggestions (empty → clinical-data path). */
  narrative?: string;
  /**
   * PHA-851: when set, the wizard opens in EDIT mode for this existing goal — it
   * skips AI suggestion generation, jumps straight to the refine stage pre-filled
   * with the goal's text/tracking question/GAS/barriers, and saves back to the
   * SAME pco_ref (upsert). When null/undefined the wizard is in ADD mode and the
   * saved goal is appended with a fresh server-assigned pco_ref.
   */
  editingGoal?: (GoalShape & { barriers?: BarrierShape[] }) | null;
  /**
   * PHA-910: the patient's already-saved goals. Used for two things:
   *   - filtering them out of the AI candidate list (they were reappearing on
   *     "add another goal"), and
   *   - predicting the pco_ref the backend will assign to a new goal, so the
   *     Reference field stops showing "pco-1" for every goal.
   * In EDIT mode the goal being edited is excluded from the dedup set, otherwise
   * regenerating suggestions would filter away the very goal being worked on.
   */
  existingGoals?: readonly GoalShape[] | null;
  /**
   * PHA-1162: true when the panel runs inside first-time onboarding — the save then skips
   * survey regeneration, which completeOnboarding performs once the patient is enrolled.
   */
  deferSurveys?: boolean;
  /** Called after approvePcmIntake succeeds so the parent can refresh patient data. */
  onLocked: () => void;
  /** Close the wizard (Cancel, or after a successful save). */
  onClose: () => void;
  /** Lifted unsaved-changes flag so the parent can guard tab switches (panel unmounts on switch). */
  onUnsavedChangesChange?: (hasUnsaved: boolean) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BARRIER_OPTIONS = ['physical', 'emotional', 'social', 'financial', 'other'] as const;

// PHA-910: the backend decides how many goals to propose (one per care-plan focus
// diagnosis + 2 whole-chart goals), so this is only a defensive upper bound to keep
// the list scannable. It must stay well above the expected count — the previous hard
// slice(0, 5) silently truncated focus-diagnosis coverage for multi-condition patients.
const MAX_SUGGESTIONS = 10;

// Simulated-progress stages for the two blocking LLM calls (no streaming from the
// backend), reusing the app-standard TimedProgress "work in progress" indicator.
const SUGGEST_PROGRESS_STEPS: ProgressStep[] = [
  { label: 'Reviewing patient context…', weight: 1 },
  { label: 'Generating person-centered goal ideas…', weight: 3 },
  { label: 'Finalizing suggestions…', weight: 1 },
];

const DETAIL_PROGRESS_STEPS: ProgressStep[] = [
  { label: 'Analyzing the chosen goal…', weight: 1 },
  { label: 'Building tracking question & GAS scale…', weight: 3 },
  { label: 'Identifying barriers…', weight: 1 },
];

// GAS = Goal Attainment Scale. Five ordered levels; each descriptor on its own full-width line.
// PHA-905: highest level first, so authoring the descriptors reads top-to-bottom in the same
// direction as the GAS chart's Y axis and scale-anchor index on the patient detail page.
const GAS_LEVELS = [
  { key: 'level_plus2',  label: '+2', caption: 'Much more than expected',       color: 'green',  numeric: 2  },
  { key: 'level_plus1',  label: '+1', caption: 'Somewhat more than expected',  color: 'teal',   numeric: 1  },
  { key: 'level_zero',   label: '0',  caption: 'Expected outcome (baseline)',  color: 'gray',   numeric: 0  },
  { key: 'level_minus1', label: '−1', caption: 'Somewhat less than expected', color: 'orange', numeric: -1 },
  { key: 'level_minus2', label: '−2', caption: 'Much less than expected', color: 'red', numeric: -2 },
] as const;

const defaultGas = (): GasShape => ({
  level_minus2: 'Much less than expected',
  level_minus1: 'Somewhat less',
  level_zero: 'Expected outcome',
  level_plus1: 'Somewhat more',
  level_plus2: 'Much more than expected',
  baseline_level: 0,
  baseline_effective_date: null,
});

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Field label — enforces minimum readable size (size="2" ≈ 14px). */
const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text as="label" size="2" weight="medium" className="block mb-1">
    {children}
  </Text>
);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PcmIntakeReviewPanel: React.FC<PcmIntakeReviewPanelProps> = ({
  patientId,
  narrative,
  editingGoal,
  existingGoals,
  deferSurveys = false,
  onLocked,
  onClose,
  onUnsavedChangesChange,
}) => {
  // PHA-910: goals that a new suggestion would duplicate. In EDIT mode the goal under
  // edit is not a duplicate of itself, so it is excluded from the set.
  const dedupeAgainst = React.useMemo(
    () => (existingGoals ?? []).filter(g => !editingGoal || g.pco_ref !== editingGoal.pco_ref),
    [existingGoals, editingGoal]
  );

  // PHA-910: the reference shown in the refine stage. In EDIT mode it's the goal's real
  // ref; in ADD mode it's the ref the server is about to mint. Display-only — the server
  // assigns the authoritative value (the wizard sends an empty ref in ADD mode), which is
  // why the field is read-only. It previously echoed the LLM's hardcoded "pco-1", so every
  // goal appeared to be number 1.
  const displayPcoRef = React.useMemo(
    () => (editingGoal ? editingGoal.pco_ref : nextPcoRef(existingGoals)),
    [editingGoal, existingGoals]
  );
  // ── Stage ──────────────────────────────────────────────────────────────────
  // PHA-851: edit mode opens directly on the refine stage, pre-filled below.
  const [stage, setStage] = useState<'suggest' | 'refine'>(editingGoal ? 'refine' : 'suggest');

  // ── Stage 1: suggestions + inline-editable chosen goal ─────────────────────
  const [generating, setGenerating] = useState(false);
  const [suggestions, setSuggestions] = useState<GoalShape[]>([]);
  const [selectedRef, setSelectedRef] = useState<string | null>(null);
  // The chosen goal's text — pre-filled from the selected suggestion, editable inline
  // (or typed from scratch when "Other" is selected).
  const [goalDraftText, setGoalDraftText] = useState('');
  // Which row is currently in edit mode (its text label is swapped for an inline input).
  const [editingRef, setEditingRef] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // ── Stage 3: the single goal + barriers ────────────────────────────────────
  // PHA-851: in edit mode, seed from the existing goal so the clinician refines it.
  const [goal, setGoal] = useState<GoalShape | null>(
    editingGoal
      ? {
          pco_ref: editingGoal.pco_ref,
          goal_text: editingGoal.goal_text,
          tracking_question: editingGoal.tracking_question ?? '',
          sort_order: editingGoal.sort_order ?? 0,
          gas: editingGoal.gas ?? defaultGas(),
        }
      : null
  );
  const [barriers, setBarriers] = useState<BarrierShape[]>(
    editingGoal?.barriers
      ? editingGoal.barriers.map((b, i) => ({ ...b, sort_order: i }))
      : []
  );
  const [saving, setSaving] = useState(false);

  // ── Unsaved-changes tracking ───────────────────────────────────────────────
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  useEffect(() => {
    onUnsavedChangesChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedChangesChange]);

  // Full-page navigation guard (SPA route changes are covered by the parent).
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // ── Generate suggestions (runs on mount, and on "Regenerate") ──────────────
  const generateSuggestions = useCallback(async () => {
    setGenerating(true);
    try {
      const ex = await patientApi.extractPcmIntake(patientId, narrative?.trim() || undefined);
      const raw: GoalShape[] = (ex?.draft as PcmIntakeDraftShape | undefined)?.goals ?? [];
      // PHA-910: drop already-saved goals BEFORE capping. Filtering after the cap
      // would let duplicates consume candidate slots and shrink the usable list.
      const goals = filterNewGoalSuggestions(raw, dedupeAgainst).slice(0, MAX_SUGGESTIONS);
      setSuggestions(goals);
      // Pre-select the first suggestion and pre-fill the editable goal text with it.
      if (goals.length > 0) {
        setSelectedRef(goals[0].pco_ref);
        setGoalDraftText(goals[0].goal_text ?? '');
      } else {
        setSelectedRef(OTHER_REF);
        setGoalDraftText('');
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to generate goal suggestions';
      toast.error(msg);
      setSuggestions([]);
      setSelectedRef(OTHER_REF);
      setGoalDraftText('');
    } finally {
      setGenerating(false);
    }
  }, [patientId, narrative, dedupeAgainst]);

  // Generate once on mount — skipped in edit mode (we already have the goal).
  const didGenerateRef = useRef(false);
  useEffect(() => {
    if (didGenerateRef.current) return;
    didGenerateRef.current = true;
    if (editingGoal) return;
    void generateSuggestions();
  }, [generateSuggestions, editingGoal]);

  // ── Select a radio option: pre-fill the editable goal text ─────────────────
  const handleSelectRef = (val: string) => {
    setSelectedRef(val);
    if (val === OTHER_REF) {
      // "Other" has no preset text → drop straight into edit mode.
      setGoalDraftText('');
      setEditingRef(OTHER_REF);
    } else {
      setGoalDraftText(suggestions.find(g => g.pco_ref === val)?.goal_text ?? '');
      setEditingRef(null);
    }
    setHasUnsavedChanges(true);
  };

  const toggleEditRef = (ref: string) => setEditingRef(prev => (prev === ref ? null : ref));

  // ── Generate GAS + barriers for the chosen (possibly edited) goal ──────────
  const handleExtractDetails = async () => {
    const text = goalDraftText.trim();
    if (!text) {
      toast.error('Choose a goal or enter your own first.');
      return;
    }
    setDetailLoading(true);
    try {
      const res = await patientApi.generatePcmGoalDetails(patientId, text);
      const g = (res?.detail as PcmIntakeDraftShape | undefined)?.goals?.[0];
      const b = (res?.detail as PcmIntakeDraftShape | undefined)?.barriers ?? [];
      setGoal({
        // PHA-910: deliberately ignore g.pco_ref. The prompt hardcodes "pco-1" on every
        // call, so trusting it made every goal display as number 1. The ref is the
        // server's to assign: empty in ADD mode, the existing ref when editing.
        pco_ref: editingGoal?.pco_ref ?? '',
        goal_text: text, // authoritative — the clinician's wording
        tracking_question: g?.tracking_question ?? '',
        sort_order: 0,
        gas: g?.gas ?? defaultGas(),
      });
      setBarriers(b.map((x, i) => ({ ...x, sort_order: i })));
      setStage('refine');
      setHasUnsavedChanges(true);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to generate goal details';
      toast.error(msg);
    } finally {
      setDetailLoading(false);
    }
  };

  // ── Stage 3 mutation helpers ───────────────────────────────────────────────
  const updateGoal = (patch: Partial<GoalShape>) => {
    setGoal(prev => (prev ? { ...prev, ...patch } : prev));
    setHasUnsavedChanges(true);
  };

  const updateGas = (key: string, value: string) => {
    setGoal(prev => (prev ? { ...prev, gas: { ...(prev.gas || defaultGas()), [key]: value } } : prev));
    setHasUnsavedChanges(true);
  };

  const addBarrier = () => {
    setBarriers(prev => [
      ...prev,
      { barrier_ref: `b-${prev.length + 1}`, barrier_category: 'other', barrier_text: '', sort_order: prev.length },
    ]);
    setHasUnsavedChanges(true);
  };

  const updateBarrier = (idx: number, patch: Partial<BarrierShape>) => {
    setBarriers(prev => prev.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
    setHasUnsavedChanges(true);
  };

  const removeBarrier = (idx: number) => {
    setBarriers(prev => prev.filter((_, i) => i !== idx));
    setHasUnsavedChanges(true);
  };

  // ── Stage 3 save: approve into transactional PCO/GAS tables ────────────────
  const handleSave = async () => {
    if (!goal || !goal.goal_text.trim()) {
      toast.error('Goal text is required.');
      return;
    }
    setSaving(true);
    try {
      const payload: PcmIntakeDraftShape = {
        // PHA-851: in EDIT mode send the goal's real pco_ref (upsert in place). In
        // ADD mode send an EMPTY ref so the backend appends a fresh unique pco_ref —
        // sending a hardcoded "pco-1" would collide with an existing goal and
        // silently overwrite it (the original PHA-851 bug).
        goals: [{
          ...goal,
          pco_ref: editingGoal ? (goal.pco_ref?.trim() || editingGoal.pco_ref) : '',
          sort_order: goal.sort_order ?? 0,
        }],
        barriers: barriers
          .filter(b => b.barrier_text.trim())
          // PHA-795: always reassign barrier_ref by position. The previous
          // `b.barrier_ref?.trim() || …` kept stale refs from state, and
          // addBarrier mints `b-${length+1}`, so after deleting an AI barrier
          // (or saving a non-contiguous set) a new barrier could reuse an
          // existing ref and violate pcm_barriers_pkey (patient_id, barrier_ref)
          // → 500. Refs aren't stable identifiers (backend deletes-all then
          // reinserts), so positional b-1..b-n is safe and collision-free.
          .map((b, i) => ({ ...b, barrier_ref: `b-${i + 1}`, sort_order: i })),
        confidence: 1,
        needs_manual_intake: false,
      };
      await patientApi.approvePcmIntake(patientId, payload, deferSurveys);
      setHasUnsavedChanges(false);
      toast.success('Voice of the Patient goal saved for care plan and survey.');
      onLocked();
      onClose();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to save the goal';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // ==========================================================================
  // STAGE 1 — Suggest + inline edit (single-select radio; selected goal editable)
  // ==========================================================================

  if (stage === 'suggest') {
    return (
      <Box>
        <Heading size="3" className="mb-1">Choose a person-centered goal</Heading>
        <Text size="2" color="gray" className="block mb-3">
          Select the goal that best reflects what matters to this patient (edit the wording as
          needed), or write your own.
        </Text>

        {generating ? (
          <Box className="my-4">
            <TimedProgress
              active={generating}
              title="Generating goal suggestions"
              estimatedDurationMs={15000}
              steps={SUGGEST_PROGRESS_STEPS}
            />
          </Box>
        ) : (
          <RadioGroup.Root value={selectedRef ?? undefined} onValueChange={handleSelectRef}>
            <Flex direction="column" gap="2">
              {suggestions.map(g => {
                const isSelected = selectedRef === g.pco_ref;
                const isEditing = editingRef === g.pco_ref;
                return (
                  <Card key={g.pco_ref} variant="surface" style={isSelected ? { borderColor: 'var(--accent-8)' } : undefined}>
                    <Flex align="center" gap="2">
                      {isEditing ? (
                        <>
                          <RadioGroup.Item value={g.pco_ref} />
                          <Box style={{ flex: 1 }}>
                            <TextField.Root size="2">
                              <TextField.Input
                                autoFocus
                                value={goalDraftText}
                                onChange={e => {
                                  setGoalDraftText(e.target.value);
                                  setHasUnsavedChanges(true);
                                }}
                                placeholder="What the patient wants to achieve, in their own words"
                              />
                            </TextField.Root>
                          </Box>
                          <IconButton variant="ghost" onClick={() => toggleEditRef(g.pco_ref)} aria-label="Done editing">
                            <CheckIcon />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <Text as="label" size="2" className="flex items-center gap-2 cursor-pointer" style={{ flex: 1 }}>
                            <RadioGroup.Item value={g.pco_ref} />
                            <span>{isSelected ? goalDraftText : g.goal_text}</span>
                          </Text>
                          {isSelected && (
                            <IconButton variant="ghost" onClick={() => toggleEditRef(g.pco_ref)} aria-label="Edit goal">
                              <Pencil1Icon />
                            </IconButton>
                          )}
                        </>
                      )}
                    </Flex>
                  </Card>
                );
              })}

              <Card variant="surface" style={selectedRef === OTHER_REF ? { borderColor: 'var(--accent-8)' } : undefined}>
                <Flex align="center" gap="2">
                  {selectedRef === OTHER_REF ? (
                    <>
                      <RadioGroup.Item value={OTHER_REF} />
                      <Box style={{ flex: 1 }}>
                        <TextField.Root size="2">
                          <TextField.Input
                            autoFocus
                            value={goalDraftText}
                            onChange={e => {
                              setGoalDraftText(e.target.value);
                              setHasUnsavedChanges(true);
                            }}
                            placeholder="Enter the patient's own goal, in their own words"
                          />
                        </TextField.Root>
                      </Box>
                    </>
                  ) : (
                    <Text as="label" size="2" className="flex items-center gap-2 cursor-pointer" style={{ flex: 1 }}>
                      <RadioGroup.Item value={OTHER_REF} />
                      <span>Other - Enter a patient-driven goal (free text)</span>
                    </Text>
                  )}
                </Flex>
              </Card>
            </Flex>
          </RadioGroup.Root>
        )}

        {detailLoading && (
          <Box className="my-4">
            <TimedProgress
              active={detailLoading}
              title="Generating tracking question & barriers"
              estimatedDurationMs={15000}
              steps={DETAIL_PROGRESS_STEPS}
            />
          </Box>
        )}

        <Separator size="4" className="my-4" />
        <Flex justify="between" align="center" gap="3">
          <Button size="2" variant="ghost" onClick={generateSuggestions} disabled={generating || detailLoading}>
            Regenerate suggestions
          </Button>
          <Flex gap="2">
            <Button size="2" variant="soft" color="gray" onClick={onClose} disabled={detailLoading}>
              Cancel
            </Button>
            <Button size="2" onClick={handleExtractDetails} disabled={generating || detailLoading || !goalDraftText.trim()}>
              {detailLoading ? 'Generating…' : 'Generate Tracking Question/Barriers'}
            </Button>
          </Flex>
        </Flex>
      </Box>
    );
  }

  // ==========================================================================
  // STAGE 3 — Refine (single goal: GAS + tracking question + barriers)
  // ==========================================================================

  if (!goal) return null;

  return (
    <Box>
      <Heading size="3" className="mb-1">Review &amp; save</Heading>
      <Text size="2" color="gray" className="block mb-3">
        Confirm the goal, its tracking question, the GAS scale, and any barriers, then save.
      </Text>

      <Card variant="surface" style={{ borderLeft: '4px solid var(--blue-7)' }}>
        <Box className="mb-3">
          <FieldLabel>Goal (patient-centered)</FieldLabel>
          <TextArea
            value={goal.goal_text}
            onChange={e => updateGoal({ goal_text: e.target.value })}
            rows={2}
            style={{ resize: 'vertical' }}
          />
        </Box>

        <Box className="mb-3">
          <FieldLabel>Tracking question</FieldLabel>
          <TextArea
            value={goal.tracking_question ?? ''}
            onChange={e => updateGoal({ tracking_question: e.target.value })}
            placeholder="e.g. How well are you managing your goal this week?"
            rows={2}
            style={{ resize: 'vertical' }}
          />
        </Box>

        <Box className="mb-3" style={{ maxWidth: 180 }}>
          <FieldLabel>Reference</FieldLabel>
          <TextField.Root size="2">
            <TextField.Input value={displayPcoRef} readOnly disabled />
          </TextField.Root>
        </Box>

        <Separator size="4" className="my-3" />

        <FieldLabel>Goal Attainment Scale (GAS)</FieldLabel>
        <Text size="2" color="gray" className="block mb-2">
          Each level on its own line — edit descriptors to fit this patient&apos;s goal.
        </Text>
        <Flex direction="column" gap="2">
          {GAS_LEVELS.map(level => (
            <Flex key={level.key} gap="2" align="start">
              <Box style={{ width: 56, flexShrink: 0, paddingTop: 8 }}>
                <Text size="2" weight="medium" align="center" className="block">
                  {level.label}
                </Text>
              </Box>
              <Box style={{ flex: 1 }}>
                <TextArea
                  value={String(goal.gas?.[level.key] ?? '')}
                  onChange={e => updateGas(level.key, e.target.value)}
                  placeholder={level.caption}
                  rows={2}
                  style={{ resize: 'vertical' }}
                />
              </Box>
            </Flex>
          ))}
        </Flex>
      </Card>

      {/* Barriers — add / edit / delete, one per line */}
      <Flex justify="between" align="center" className="mb-3 mt-4">
        <Flex align="baseline" gap="2">
          <Heading size="3">Barriers</Heading>
          <Text size="2" color="gray">optional</Text>
        </Flex>
        <Button size="1" variant="soft" onClick={addBarrier}>
          <PlusIcon /> Add barrier
        </Button>
      </Flex>

      {barriers.length > 0 && (
        <Flex direction="column" gap="2" className="mb-4">
          {barriers.map((b, idx) => (
            <Flex key={`barrier-${idx}`} gap="2" align="start">
              <Select.Root
                value={b.barrier_category}
                onValueChange={val => updateBarrier(idx, { barrier_category: val })}
              >
                <Select.Trigger style={{ minWidth: 120 }} />
                <Select.Content>
                  {BARRIER_OPTIONS.map(opt => (
                    <Select.Item key={opt} value={opt}>
                      {opt}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
              <Box style={{ flex: 1 }}>
                <TextArea
                  value={b.barrier_text}
                  onChange={e => updateBarrier(idx, { barrier_text: e.target.value })}
                  placeholder="What gets in the way of reaching this goal"
                  rows={1}
                  style={{ resize: 'vertical' }}
                />
              </Box>
              <IconButton
                color="red"
                variant="ghost"
                onClick={() => removeBarrier(idx)}
                aria-label={`Remove barrier ${idx + 1}`}
              >
                <TrashIcon />
              </IconButton>
            </Flex>
          ))}
        </Flex>
      )}

      {detailLoading && (
        <Box className="mb-3">
          <TimedProgress
            active={detailLoading}
            title="Regenerating goal details"
            estimatedDurationMs={15000}
            steps={DETAIL_PROGRESS_STEPS}
          />
        </Box>
      )}

      <Separator size="4" className="my-4" />
      <Flex justify="between" align="center" gap="3">
        <Button size="2" variant="ghost" onClick={() => setStage('suggest')} disabled={saving}>
          ← Back
        </Button>
        <Flex gap="2">
          <Button size="2" variant="soft" color="gray" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button size="2" onClick={handleSave} disabled={saving || !goal.goal_text.trim()}>
            {saving ? 'Saving…' : 'Save for Care plan and survey'}
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
};

export default PcmIntakeReviewPanel;
